import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { PartnerService } from '@duck/api-definitions'
import { ClipboardDocumentListIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import { Breadcrumbs, Card, CardContent, Tabs, TabsList, TabsTrigger, TabsContent } from '@duck/ui'
import { apis } from '@modules/apiClients'
import { useTar } from '@/modules'
import { useAdminOrganization } from '@/hooks/useAdminOrganization'
import { adminOrganizationsQueryKeys } from '@/hooks/useAdminOrganizations'
import { organizationsQueryKeys } from '@/storeHooks/useOrganizations'
import { useLogoUrlValidation } from '@/hooks/useLogoUrlValidation'
import { usePartnerDashboardRows } from '@/hooks/usePartnerDashboardRows'
import { PartnerOrgTypeContext, usePartnerTar } from '@/hooks/usePartnerTar'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { StandardLoader } from '@/components/StandardLoader'
import { NoDataEmptyState } from '@/components/EmptyStates'
import { CohortsOverviewTable } from '@/components/PartnerDashboard/CohortsOverviewTable'
import { PartnerDashboardTable } from '@/components/PartnerDashboard/PartnerDashboardTable'
import {
  OrganizationDetailsSection,
  OrganizationBrandingSection,
  OrganizationSettingsSection,
  type OrganizationFieldValue,
} from '../OrganizationSections'

const BLANK_ORGANIZATION: Partial<PartnerService.Organization> = {
  name: '',
  description: '',
  type: PartnerService.OrganizationType.Company,
  active: true,
  logoURL: '',
  logoSquareURL: '',
  logoCanvas: PartnerService.OrganizationLogoCanvas.Plaque,
}

// Tab lives in the URL (/{id}/{tab}); unknown values fall back to the default.
const TABS = ['details', 'branding', 'settings', 'cohorts', 'assignments'] as const

interface OrganizationDetailPageProps {
  id: string
  tab?: string
}

export const OrganizationDetailPage: React.FC<OrganizationDetailPageProps> = ({ id, tab }) => {
  const { organization } = useAdminOrganization(id)
  // Admins have no org type of their own; tP must resolve the viewed org's type
  // so school orgs read Classes/Students and companies read Positions/Candidates.
  return (
    <PartnerOrgTypeContext.Provider value={organization?.type}>
      <OrganizationDetailContent id={id} tab={tab} />
    </PartnerOrgTypeContext.Provider>
  )
}

const OrganizationDetailContent: React.FC<OrganizationDetailPageProps> = ({ id, tab }) => {
  const { t } = useTar()
  const { tP } = usePartnerTar()
  const navigate = useNavigate()
  const activeTab = TABS.includes(tab as typeof TABS[number]) ? tab : 'details'
  const queryClient = useQueryClient()
  const isCreateMode = id === 'new'

  const { organization, isLoading, isError, notFound, refetch } = useAdminOrganization(id)
  const dashboard = usePartnerDashboardRows(isCreateMode ? undefined : id)

  useDocumentTitle(isCreateMode ? 'New Organization' : organization?.name ?? id, 'admin')

  const [formData, setFormData] = useState<Partial<PartnerService.Organization>>(BLANK_ORGANIZATION)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const wordmarkValidation = useLogoUrlValidation(formData.logoURL ?? '', 'wordmark')
  const squareValidation = useLogoUrlValidation(formData.logoSquareURL ?? '', 'square')
  const hasValidationError = wordmarkValidation.status === 'error' || squareValidation.status === 'error'

  useEffect(() => {
    setFormData(isCreateMode ? BLANK_ORGANIZATION : { ...organization })
    setSaveError(null)
    setIsSaving(false)
  }, [organization, isCreateMode])

  const handleInputChange = (field: keyof PartnerService.Organization, value: OrganizationFieldValue) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    setSaveError(null)
    if (!formData.name || !formData.type) {
      setSaveError(t('organizations.validationNameTypeRequired'))
      return
    }

    try {
      setIsSaving(true)
      if (isCreateMode) {
        const response = await apis.partnerService.organizationsPost(
          formData as PartnerService.CreateOrganizationRequest,
        )
        toast.success(t('organizations.organizationCreated'))
        void queryClient.invalidateQueries({ queryKey: adminOrganizationsQueryKeys.all() })
        void navigate({ to: '/admin/organizations/$id/{-$tab}', params: { id: response.data.data.id, tab: undefined } })
      } else {
        await apis.partnerService.organizationsOrganizationIdPut(
          id,
          formData as PartnerService.UpdateOrganizationRequest,
        )
        toast.success(t('organizations.organizationUpdated'))
        void queryClient.invalidateQueries({ queryKey: adminOrganizationsQueryKeys.all() })
        void queryClient.invalidateQueries({ queryKey: organizationsQueryKeys.all() })
        void refetch()
      }
    } catch (error: unknown) {
      console.error('Failed to save organization:', error)
      const errorMessage =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        t('organizations.saveFailed')
      setSaveError(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  const title = isCreateMode ? t('organizations.newOrganization') : organization?.name ?? id

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('challenges.breadcrumbAdmin'), href: '/admin' },
          { label: t('navigation.organizations'), href: '/admin/organizations' },
          { label: title },
        ]}
        renderLink={(item, children) => <Link to={item.href}>{children}</Link>}
      />

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <StandardLoader />
        </div>
      )}

      {isError && !isLoading && (
        <div className="rounded-md bg-error/10 border border-error/20 p-4">
          <div className="text-error text-sm">{t('common.error')}</div>
        </div>
      )}

      {notFound && !isLoading && (
        <NoDataEmptyState
          title={t('organizations.detailNotFound')}
          buttonText={t('navigation.organizations')}
          onButtonClick={() => { void navigate({ to: '/admin/organizations' }) }}
        />
      )}

      {!isLoading && !isError && !notFound && (
        <>
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-2xl font-bold text-foreground truncate">{title}</h2>
            <div className="flex items-center gap-3 shrink-0">
              <button
                data-testid="organization-detail-save"
                onClick={() => { void handleSave() }}
                disabled={isSaving || hasValidationError}
                className="px-4 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSaving && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {isSaving ? t('common.saving') : isCreateMode ? t('common.create') : t('common.save')}
              </button>
            </div>
          </div>

          <Tabs
            value={activeTab}
            // Manual activation: with automatic, Radix fires onValueChange on mousedown
            // and again on focus (value prop lags the router), pushing two history entries.
            activationMode="manual"
            onValueChange={(next) => { void navigate({ to: '/admin/organizations/$id/{-$tab}', params: { id, tab: next === 'details' ? undefined : next } }) }}
            className="w-full"
          >
            <TabsList className={`flex w-full justify-start overflow-x-auto md:grid md:justify-center ${isCreateMode ? 'md:grid-cols-3' : 'md:grid-cols-5'}`}>
              <TabsTrigger value="details">{t('common.details')}</TabsTrigger>
              <TabsTrigger value="branding">{t('organizations.branding')}</TabsTrigger>
              <TabsTrigger value="settings">{t('common.settings')}</TabsTrigger>
              {!isCreateMode && <TabsTrigger value="cohorts">{tP('cohorts')}</TabsTrigger>}
              {!isCreateMode && <TabsTrigger value="assignments">{t('partner.assignments')}</TabsTrigger>}
            </TabsList>

            <TabsContent value="details" className="mt-4">
              <OrganizationDetailsSection formData={formData} onChange={handleInputChange} isSaving={isSaving} />
            </TabsContent>

            <TabsContent value="branding" className="mt-4">
              <OrganizationBrandingSection
                formData={formData}
                onChange={handleInputChange}
                isSaving={isSaving}
                wordmarkValidation={wordmarkValidation}
                squareValidation={squareValidation}
              />
            </TabsContent>

            <TabsContent value="settings" className="mt-4">
              <OrganizationSettingsSection formData={formData} onChange={handleInputChange} isSaving={isSaving} />
            </TabsContent>

            {!isCreateMode && (
              <TabsContent value="cohorts" className="mt-4">
                {dashboard.isLoading ? (
                  <div className="flex items-center justify-center py-16"><StandardLoader /></div>
                ) : dashboard.cohortsWithStats.length === 0 ? (
                  <Card>
                    <CardContent className="pt-6">
                      <NoDataEmptyState
                        icon={UserGroupIcon}
                        title={tP('noCohortsYet')}
                        description={tP('noCohortsDescription')}
                      />
                    </CardContent>
                  </Card>
                ) : (
                  <CohortsOverviewTable cohortsWithStats={dashboard.cohortsWithStats} organizationId={id} />
                )}
              </TabsContent>
            )}

            {!isCreateMode && (
              <TabsContent value="assignments" className="mt-4">
                {dashboard.isLoading ? (
                  <div className="flex items-center justify-center py-16"><StandardLoader /></div>
                ) : dashboard.flatRows.length === 0 ? (
                  <Card>
                    <CardContent className="pt-6">
                      <NoDataEmptyState
                        icon={ClipboardDocumentListIcon}
                        title={t('partner.noAssignmentsYet')}
                        description={tP('noAssignmentsDescription')}
                      />
                    </CardContent>
                  </Card>
                ) : (
                  <PartnerDashboardTable rows={dashboard.flatRows} persons={dashboard.persons} />
                )}
              </TabsContent>
            )}
          </Tabs>

          {saveError && (
            <div className="p-3 bg-error/10 border border-error/20 rounded-md">
              <p className="text-sm text-error">{saveError}</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
